import { useState, useEffect } from 'react';
import type { Datacenter } from '../types';

interface DatacenterFormModalProps {
  datacenter?: Datacenter | null;
  onClose: () => void;
  onSave: (datacenter: Partial<Datacenter>) => void;
}

const provinces = ['北京市', '上海市', '广东省', '浙江省', '江苏省', '四川省'];
const cities: Record<string, string[]> = {
  '北京市': ['北京市'],
  '上海市': ['上海市'],
  '广东省': ['广州市', '深圳市', '东莞市', '佛山市'],
  '浙江省': ['杭州市', '宁波市', '温州市'],
  '江苏省': ['南京市', '苏州市', '无锡市'],
  '四川省': ['成都市', '绵阳市'],
};
const districts: Record<string, string[]> = {
  '北京市': ['海淀区', '朝阳区', '东城区', '西城区'],
  '上海市': ['浦东新区', '黄浦区', '徐汇区', '静安区'],
  '广州市': ['天河区', '越秀区', '海珠区'],
  '深圳市': ['南山区', '福田区', '罗湖区'],
  '杭州市': ['西湖区', '滨江区', '余杭区'],
  '南京市': ['玄武区', '秦淮区', '建邺区'],
  '成都市': ['武侯区', '锦江区', '青羊区'],
};

const remoteDatacenters = [
  { id: 'remote-bj-001', name: '北京机房-001' },
  { id: 'remote-sh-001', name: '上海机房-001' },
  { id: 'remote-sz-001', name: '深圳机房-001' },
  { id: 'remote-gz-001', name: '广州机房-001' },
  { id: 'remote-hz-001', name: '杭州机房-001' },
];

export default function DatacenterFormModal({ datacenter, onClose, onSave }: DatacenterFormModalProps) {
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    remoteId: '',
    connectionUrl: '',
    apiKey: '',
    province: '',
    city: '',
    district: '',
    address: '',
    description: '',
    type: 'self-built' as 'self-built' | 'hosted' | 'rented',
    bandwidth: '',
    isp: '',
    buildDate: '',
    contactName: '',
    contactPhone: '',
  });

  const [availableCities, setAvailableCities] = useState<string[]>([]);
  const [availableDistricts, setAvailableDistricts] = useState<string[]>([]);

  useEffect(() => {
    if (datacenter) {
      setFormData({
        name: datacenter.name,
        code: datacenter.code,
        remoteId: datacenter.remoteId,
        connectionUrl: datacenter.connectionUrl,
        apiKey: datacenter.apiKey,
        province: datacenter.location.province,
        city: datacenter.location.city,
        district: datacenter.location.district,
        address: datacenter.location.address,
        description: datacenter.description,
        type: datacenter.type,
        bandwidth: datacenter.bandwidth,
        isp: datacenter.isp,
        buildDate: datacenter.buildDate,
        contactName: datacenter.contact.name,
        contactPhone: datacenter.contact.phone,
      });
      setAvailableCities(cities[datacenter.location.province] || []);
      setAvailableDistricts(districts[datacenter.location.city] || []);
    }
  }, [datacenter]);

  const handleProvinceChange = (province: string) => {
    setFormData({ ...formData, province, city: '', district: '' });
    setAvailableCities(cities[province] || []);
    setAvailableDistricts([]);
  };

  const handleCityChange = (city: string) => {
    setFormData({ ...formData, city, district: '' });
    setAvailableDistricts(districts[city] || []);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      ...datacenter,
      name: formData.name,
      code: formData.code,
      remoteId: formData.remoteId,
      connectionUrl: formData.connectionUrl,
      apiKey: formData.apiKey,
      location: {
        province: formData.province,
        city: formData.city,
        district: formData.district,
        address: formData.address,
      },
      description: formData.description,
      type: formData.type,
      bandwidth: formData.bandwidth,
      isp: formData.isp,
      buildDate: formData.buildDate,
      contact: {
        name: formData.contactName,
        phone: formData.contactPhone,
      },
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">
            {datacenter ? '编辑机房' : '新建机房'}
          </h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                机房名称 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                placeholder="请输入机房名称"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                机房代码 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                placeholder="例如：JN-BS-01"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                机房ID <span className="text-red-500">*</span>
              </label>
              <select
                required
                value={formData.remoteId}
                onChange={(e) => setFormData({ ...formData, remoteId: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                <option value="">请选择机房ID</option>
                {remoteDatacenters.map((dc) => (
                  <option key={dc.id} value={dc.id}>
                    {dc.name} ({dc.id})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                机房类型 <span className="text-red-500">*</span>
              </label>
              <select
                required
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value as any })}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                <option value="self-built">自建</option>
                <option value="hosted">托管</option>
                <option value="rented">租用</option>
              </select>
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                连接地址 <span className="text-red-500">*</span>
              </label>
              <input
                type="url"
                required
                value={formData.connectionUrl}
                onChange={(e) => setFormData({ ...formData, connectionUrl: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                placeholder="https://example.com"
              />
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                API KEY <span className="text-red-500">*</span>
              </label>
              <input
                type="password"
                required
                value={formData.apiKey}
                onChange={(e) => setFormData({ ...formData, apiKey: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                placeholder="请输入API KEY"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                省份 <span className="text-red-500">*</span>
              </label>
              <select
                required
                value={formData.province}
                onChange={(e) => handleProvinceChange(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                <option value="">请选择省份</option>
                {provinces.map((province) => (
                  <option key={province} value={province}>
                    {province}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                城市 <span className="text-red-500">*</span>
              </label>
              <select
                required
                value={formData.city}
                onChange={(e) => handleCityChange(e.target.value)}
                disabled={!formData.province}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:bg-gray-100"
              >
                <option value="">请选择城市</option>
                {availableCities.map((city) => (
                  <option key={city} value={city}>
                    {city}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                区县 <span className="text-red-500">*</span>
              </label>
              <select
                required
                value={formData.district}
                onChange={(e) => setFormData({ ...formData, district: e.target.value })}
                disabled={!formData.city}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:bg-gray-100"
              >
                <option value="">请选择区县</option>
                {availableDistricts.map((district) => (
                  <option key={district} value={district}>
                    {district}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                详细地址 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                placeholder="请输入详细地址"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                网络出口带宽 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={formData.bandwidth}
                onChange={(e) => setFormData({ ...formData, bandwidth: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                placeholder="例如：10Gbps"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                运营商信息 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={formData.isp}
                onChange={(e) => setFormData({ ...formData, isp: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                placeholder="例如：中国电信"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                建设日期 <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                required
                value={formData.buildDate}
                onChange={(e) => setFormData({ ...formData, buildDate: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                联系人 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={formData.contactName}
                onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                placeholder="请输入联系人姓名"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                联系电话 <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                required
                value={formData.contactPhone}
                onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                placeholder="请输入联系电话"
              />
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                机房描述
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                placeholder="请输入机房描述"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
            >
              取消
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap"
            >
              保存
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
